import { RunStatus } from "@kaggle/components";
import { Kaggle } from "@kaggle/sdk";
import BatchedRunStatus from "../../../types/BatchedRunStatus";
import { BatchedRunActionKey, RunActionKey } from "../../actions/ActionKeys";
import {
  BatchedRunAction,
  BatchedRunUploadingAction,
} from "../../actions/batchedRun";
import { RunsAction } from "../../actions/runs";

import KernelVersionType = Kaggle.Sdk.Kernels.KernelVersionType;

interface State {
  id: number | null;
  uploadProgress: number | null;
  status: BatchedRunStatus;
  errorMessage: string | null;
  showProgressSnackbar: boolean;
  versionType: KernelVersionType.BATCH | KernelVersionType.QUICK | null;
}

/** ID of the run being committed and executed on the background. */
const id = (state: State, action: BatchedRunAction | RunsAction) => {
  switch (action.type) {
    case BatchedRunActionKey.SET_BATCHED_RUN:
      return action.payload.run;
    case BatchedRunActionKey.REMOVE_BATCHED_RUN:
      return null;
    default:
      return state.id;
  }
};

/** Maps run statuses to batched run statuses. */
const mapRunStatusToCommitStatus = (runStatus: RunStatus): BatchedRunStatus => {
  switch (runStatus) {
    case RunStatus.ERROR:
      return BatchedRunStatus.ERROR;
    case RunStatus.NEW_SCRIPT:
      return BatchedRunStatus.NONE;
    case RunStatus.QUEUED:
      return BatchedRunStatus.COMMITTING;
    case RunStatus.RUNNING:
      return BatchedRunStatus.RUNNING;
    case RunStatus.CANCEL_REQUESTED:
      return BatchedRunStatus.CANCELLING;
    case RunStatus.COMPLETE:
      return BatchedRunStatus.COMPLETE;
    case RunStatus.CANCEL_ACKNOWLEDGED:
      return BatchedRunStatus.CANCELLED;
  }
};

/**
 * Status of the run being commited and executed.
 *
 * This status is used to track interim states of a run.  To ensure a
 * responsive application, we create artificial status of a run such
 * as "committing", "cancelling". These status are not properties of a run but
 * represent interim states of a run.
 */
const status = (state: State, action: BatchedRunAction | RunsAction) => {
  switch (action.type) {
    case BatchedRunActionKey.SET_BATCHED_RUN_STATUS:
      return action.payload.status;
    case BatchedRunActionKey.REMOVE_BATCHED_RUN:
      return BatchedRunStatus.NONE;
    case RunActionKey.SET_RUN_STATUS:
      if (action.payload.id === state.id) {
        return mapRunStatusToCommitStatus(action.payload.status);
      }
      return state.status;
    default:
      return state.status;
  }
};

const errorMessage = (state: State, action: BatchedRunAction | RunsAction) => {
  switch (action.type) {
    case BatchedRunActionKey.SET_BATCHED_RUN_ERROR_MESSAGE:
      return action.payload.message;
    case BatchedRunActionKey.REMOVE_BATCHED_RUN:
      return null;
    default:
      return state.errorMessage;
  }
};

/** State of the upload */
export const uploadProgress = (
  state: number | null,
  action: BatchedRunAction | RunsAction
) => {
  switch (action.type) {
    case BatchedRunActionKey.SET_BATCHED_RUN_STATUS:
      if (action.payload.status === BatchedRunStatus.UPLOADING) {
        return (action as BatchedRunUploadingAction).payload.progress;
      }
      return null;
    default:
      return state;
  }
};

export const showProgressSnackbar = (
  state: boolean,
  action: BatchedRunAction
) => {
  switch (action.type) {
    case BatchedRunActionKey.SET_SHOW_BATCHED_RUN_PROGRESS_SNACKBAR:
      return action.payload.show;
    default:
      return state;
  }
};

export const versionType = (
  state: KernelVersionType.BATCH | KernelVersionType.QUICK | null = null,
  action: BatchedRunAction | RunsAction
): KernelVersionType.BATCH | KernelVersionType.QUICK | null => {
  switch (action.type) {
    case BatchedRunActionKey.SET_BATCHED_RUN:
      return action.payload.versionType;
    default:
      return state;
  }
};

/** State of the run being committed and executed. */
export const batchedRun = (
  state: State = {
    id: null,
    status: BatchedRunStatus.NONE,
    uploadProgress: null,
    errorMessage: null,
    showProgressSnackbar: false,
    versionType: null,
  },
  action: BatchedRunAction | RunsAction
) => ({
  id: id(state, action),
  status: status(state, action),
  uploadProgress: uploadProgress(state.uploadProgress, action),
  errorMessage: errorMessage(state, action),
  showProgressSnackbar: showProgressSnackbar(
    state.showProgressSnackbar,
    action as BatchedRunAction
  ),
  versionType: versionType(state.versionType, action),
});
